/**
 * Tiempo real por Server-Sent Events.
 *
 * `EventSource` se encarga de reconectar solo cuando la conexion se cae; este
 * modulo solo traduce los eventos al contrato `RealtimeEvent` y los reparte
 * entre los componentes suscritos, notificando ademas el estado de la conexion
 * para poder avisar al agente cuando se pierde.
 */
import type { RealtimeEvent } from '@shared/api-types.ts';
import { eventsUrl } from './api.ts';

export type SseStatus = 'connecting' | 'open' | 'reconnecting' | 'offline';

export type SseStatusListener = (status: SseStatus) => void;
export type RealtimeListener = (event: RealtimeEvent) => void;

const EVENT_TYPES = [
  'message.created',
  'message.status',
  'conversation.updated',
  'conversation.assigned',
  'typing',
] as const satisfies readonly RealtimeEvent['type'][];

function isRealtimeType(value: string): value is RealtimeEvent['type'] {
  return (EVENT_TYPES as readonly string[]).includes(value);
}

export class RealtimeHub {
  private source: EventSource | null = null;
  private token: string | null = null;
  private tenantId: string | null = null;
  private status: SseStatus = 'offline';
  private readonly listeners = new Set<RealtimeListener>();
  private readonly statusListeners = new Set<SseStatusListener>();

  getStatus(): SseStatus {
    return this.status;
  }

  /** Abre (o reutiliza) la conexion para ese token y organizacion. */
  connect(token: string, tenantId?: string): void {
    if (this.source && this.token === token && this.tenantId === (tenantId ?? null)) return;
    this.disconnect();
    this.token = token;
    this.tenantId = tenantId ?? null;

    if (typeof EventSource === 'undefined') {
      this.setStatus('offline');
      return;
    }

    this.setStatus('connecting');
    const source = new EventSource(eventsUrl(token, tenantId));
    this.source = source;

    source.onopen = () => {
      this.setStatus('open');
    };

    source.onerror = () => {
      // `EventSource` reintenta por su cuenta: CONNECTING significa "volviendo",
      // CLOSED que se ha rendido (o que hemos cerrado nosotros).
      this.setStatus(source.readyState === EventSource.CLOSED ? 'offline' : 'reconnecting');
    };

    // El backend puede emitir eventos con nombre (`event: message.created`) o
    // genericos (`data: {"type":"message.created"}`). Se atienden ambos.
    source.onmessage = (event: MessageEvent) => {
      this.handle(event.data, null);
    };

    for (const type of EVENT_TYPES) {
      source.addEventListener(type, (event: Event) => {
        const data: unknown = event instanceof MessageEvent ? event.data : null;
        this.handle(data, type);
      });
    }
  }

  disconnect(): void {
    if (this.source) {
      this.source.close();
      this.source = null;
    }
    this.token = null;
    this.tenantId = null;
    this.setStatus('offline');
  }

  subscribe(listener: RealtimeListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  subscribeStatus(listener: SseStatusListener): () => void {
    this.statusListeners.add(listener);
    listener(this.status);
    return () => {
      this.statusListeners.delete(listener);
    };
  }

  private setStatus(next: SseStatus): void {
    if (next === this.status) return;
    this.status = next;
    for (const listener of this.statusListeners) listener(next);
  }

  private handle(data: unknown, fallbackType: RealtimeEvent['type'] | null): void {
    if (typeof data !== 'string' || data.length === 0) return;

    let parsed: unknown;
    try {
      parsed = JSON.parse(data) as unknown;
    } catch {
      return;
    }
    if (!parsed || typeof parsed !== 'object') return;

    const candidate = parsed as { type?: unknown };
    const type =
      typeof candidate.type === 'string' && isRealtimeType(candidate.type)
        ? candidate.type
        : fallbackType;
    if (!type) return;

    // La forma del evento viene garantizada por el contrato `RealtimeEvent`;
    // lo unico que puede faltar es el campo `type`, que se rellena aqui.
    const event = { ...(parsed as object), type } as RealtimeEvent;

    for (const listener of this.listeners) listener(event);
  }
}

/** Conexion unica compartida por toda la aplicacion. */
export const realtimeHub = new RealtimeHub();

export const SSE_STATUS_LABELS: Record<SseStatus, string> = {
  connecting: 'Conectando…',
  open: 'En directo',
  reconnecting: 'Reconectando…',
  offline: 'Sin conexion',
};
